import { defineComponent as _defineComponent } from 'vue'
import type { Maybe } from '@/helpers/types';

import {
	GetSingleStepTeamEventsDocument,
	GetSingleStepTeamEventsQuery,
	GetSingleStepTeamEventsQueryVariables,
} from '@/features/sports/graphql/queries/GetSingleStepTeamEvents.query';
import { SingleStepContext } from '@/features/sports/helpers/config';
import {
	SportEventBucketsEdgeV2,
	SportEventMatrixCompetitionColumn,
	SportEventMatrixCompetitionColumns,
	SportEventMatrixPackageRow,
} from '@/@types/graphql-types';
import CompetitionEventsItem from '@/features/sports/components/overview/CompetitionEventsItem.vue';
import CompetitionLogo from '@/features/sports/components/ui/CompetitionLogo.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useQuery } from '@/helpers/composables/useApollo';
import { useLanguageStore, useRoutingStore } from '@/helpers/composables/useStores';
import { computed, ref, watch } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'TeamCompetitionsCalendar',
  props: {
    context: null,
    competitionEvents: null,
    startAt: null,
    competitor: null,
    selectedCompetitions: null
  },
  emits: ["mounted", "competitions", "toggleShowProviderFilters"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'mounted', value: string[]): void;
	(e: 'competitions', value: SportEventMatrixCompetitionColumn[]): void;
	(e: 'toggleShowProviderFilters'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	context: SingleStepContext;
	competitionEvents: SportEventBucketsEdgeV2[];

	// DATA FETCHING
	startAt?: Maybe<{ from?: string; to?: string }>;
	competitor: { id: string; content: { name: string; iconUrl?: Maybe<string> } };

	// FILTERING
	selectedCompetitions: string[];
};





const { country, language } = useLanguageStore();
const { activeRoute, activeDomain } = useRoutingStore();

const queryVariables = computed<GetSingleStepTeamEventsQueryVariables>(() => {
	return {
		language: language.value,
		country: country.value,
		sport: props.context.sportType,
		site: activeDomain.value,
		filter: {
			startAt: props.startAt,
			competitorIds: [props.competitor.id],
		},
	};
});

const { result, onResult } = useQuery<GetSingleStepTeamEventsQuery, GetSingleStepTeamEventsQueryVariables>(
	GetSingleStepTeamEventsDocument,
	queryVariables
);

const competitionColumns = computed<SportEventMatrixCompetitionColumn[] | undefined>(() => {
	return (result.value?.sportEventMatrix as SportEventMatrixCompetitionColumns)?.competitions;
});

onResult(() => {
	if (competitionColumns.value?.length === 0 || !competitionColumns.value) return;
	emit('mounted', [competitionColumns.value[0].competition.id]);
	emit('competitions', competitionColumns.value);
});

const unfilteredCompetitionEvents = ref<SportEventBucketsEdgeV2[]>([]);
const hasStoredInitialEvents = ref(false);

watch(
	() => props.competitionEvents,
	events => {
		if (!hasStoredInitialEvents.value && events.length) {
			unfilteredCompetitionEvents.value = [...events];
			hasStoredInitialEvents.value = true;
		}
	},
	{ immediate: true, deep: true }
);

watch(activeRoute, () => {
	hasStoredInitialEvents.value = false;
});

const competitionColumnsFromEvents = computed(() => {
	return unfilteredCompetitionEvents.value
		.filter(({ key, node }) => !!node.edges?.length && key.competition != null)
		.map(({ key, node }) => ({ competition: key.competition, count: node.edges?.length }))
		.filter(el => !!el);
});

const showProviderFilters = ref(false);

function toggleProviderFilters() {
	showProviderFilters.value = !showProviderFilters.value;
	emit('toggleShowProviderFilters');
}

const selectedCompetitionsPackages = computed(() => {
	const packageList = competitionColumns.value
		?.filter(({ competition }) => props.selectedCompetitions.includes(competition.id))
		.flatMap(({ packages }) => packages)
		// if multiple competitions are available on the same provider,
		// combine the providers into one with a totalled match count
		.reduce((list: Record<number, SportEventMatrixPackageRow>, row) => {
			if (!list[row.package.packageId]) {
				list[row.package.packageId] = { ...row };
			} else {
				list[row.package.packageId].count += row.count;
			}

			return list;
		}, {});

	return Object.values(packageList ?? {});
});

return { __sfc: true,props, emit, country, language, activeRoute, activeDomain, queryVariables, result, onResult, competitionColumns, unfilteredCompetitionEvents, hasStoredInitialEvents, competitionColumnsFromEvents, showProviderFilters, toggleProviderFilters, selectedCompetitionsPackages, CompetitionEventsItem, CompetitionLogo, HiddenHorizontalScrollbar, ProviderIcon, faChevronUp, faChevronDown }
}

})